/* @flow */
import invariant from 'invariant';
import { Map } from 'immutable';
import type { OrganizationClassesFetchOptions } from 'clients/api/organizationClasses';
import { fetch } from 'clients/api/organizationClasses';

export type OrganizationClassesTable = {
  page: number,
  groups: PartialGroup[],
  loading: boolean,
  allLoaded: boolean,
};

export type State = Map<number, OrganizationClassesTable>;
export const initialState: State = new Map();

export default function reducer(state: State = initialState, action: *): State {
  switch (action.type) {
    case 'organizationClassTables/INITIALIZE_TABLE': {
      return state.set(action.organizationId, {
        page: 1,
        groups: [],
        loading: false,
        allLoaded: false,
      });
    }

    case 'organizationClassTables/LOAD_ORGANIZATION_CLASSES_SUCCESS': {
      const table = state.get(action.organizationId);

      invariant(table, 'organizationClassTables/LOAD_ORGANIZATION_CLASSES_SUCCESS missing table');

      return state.set(action.organizationId, {
        loading: false,
        page: action.options.page,
        allLoaded: action.groups.length < action.options.perPage,
        groups: action.options.page === 1 ? action.groups : [].concat(table.groups, action.groups),
      });
    }

    default: {
      return state;
    }
  }
}

export type LoadOrganizationClassesSuccessAction = {
  type: 'organizationClassTables/LOAD_ORGANIZATION_CLASSES_SUCCESS',
  options: OrganizationClassesFetchOptions,
  organizationId: number,
  groups: PartialGroup[],
};

export function loadOrganizationClassesSuccess(
  organizationId: number,
  groups: PartialGroup[],
  options: OrganizationClassesFetchOptions
): LoadOrganizationClassesSuccessAction {
  return {
    type: 'organizationClassTables/LOAD_ORGANIZATION_CLASSES_SUCCESS',
    organizationId,
    options,
    groups,
  };
}

export type InitializeTableAction = {
  type: 'organizationClassTables/INITIALIZE_TABLE',
  organizationId: number,
};

export function initializeTable(organizationId: number): InitializeTableAction {
  return {
    type: 'organizationClassTables/INITIALIZE_TABLE',
    organizationId,
  };
}

export function loadOrganizationClasses(
  organizationId: number,
  options: OrganizationClassesFetchOptions
) {
  return async (dispatch: Function) => {
    try {
      const classes = await fetch(organizationId, options);
      dispatch(loadOrganizationClassesSuccess(organizationId, classes, options));
    } catch (e) {
      console.error('loadOrganizationClasses failure', e);
    }
  };
}

export type OrganizationClassesTablesAction =
  | InitializeTableAction
  | LoadOrganizationClassesSuccessAction;
